import React, { useState } from 'react'
import { Upload, X, Star, Maximize2, Info } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, ImageLightbox } from '../ui'

interface VehiclePhotosUploadProps {
  photos: File[]
  onPhotosChange: (photos: File[]) => void
  primaryPhotoIndex: number
  onPrimaryPhotoChange: (index: number) => void
  error?: string
}

export const VehiclePhotosUpload: React.FC<VehiclePhotosUploadProps> = ({
  photos,
  onPhotosChange,
  primaryPhotoIndex,
  onPrimaryPhotoChange,
  error,
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const openLightbox = (index: number) => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    const imageFiles = files.filter((file) => file.type.startsWith('image/'))

    if (imageFiles.length !== files.length) return

    const remainingSlots = 15 - photos.length
    if (imageFiles.length > remainingSlots) return

    onPhotosChange([...photos, ...imageFiles])
  }

  const removePhoto = (index: number) => {
    const newPhotos = photos.filter((_, i) => i !== index)
    onPhotosChange(newPhotos)

    if (newPhotos.length === 0) {
      onPrimaryPhotoChange(0)
    } else if (index === primaryPhotoIndex) {
      onPrimaryPhotoChange(0)
    } else if (index < primaryPhotoIndex) {
      onPrimaryPhotoChange(primaryPhotoIndex - 1)
    }
  }

  const setPrimaryPhoto = (index: number) => {
    onPrimaryPhotoChange(index)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <Upload className="h-5 w-5 mr-2 text-accent" />
            Fotos do Veículo
          </div>
          <span
            className={`text-sm font-medium tabular-nums ${
              photos.length >= 15
                ? 'text-red-600'
                : photos.length >= 10
                  ? 'text-amber-600'
                  : 'text-muted-foreground'
            }`}
          >
            {photos.length} / 15 fotos
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            disabled={photos.length >= 15}
            className="block w-full text-sm text-muted-foreground
              file:mr-4 file:py-2 file:px-4
              file:rounded-full file:border-0
              file:text-sm file:font-semibold
              file:bg-accent/10 file:text-accent
              hover:file:bg-accent/20
              disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {error && <p className="mt-2 text-red-600 text-sm">{error}</p>}
          <p className="mt-2 text-sm text-muted-foreground">
            Adicione até 15 fotos. Formatos aceitos: JPG, PNG, WEBP
          </p>
        </div>

        {photos.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo, index) => (
              <div key={index} className="relative group">
                <div
                  className={`aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                    index === primaryPhotoIndex
                      ? 'border-accent ring-2 ring-accent/30'
                      : 'border-border'
                  }`}
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={URL.createObjectURL(photo)}
                    alt={`Foto ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />

                  {/* Overlay de visualização */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                    <Maximize2 className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div>

                {/* Botão de remover */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    removePhoto(index)
                  }}
                  className="absolute top-2 right-2 bg-red-600 text-white p-1.5 rounded-full hover:bg-red-700 transition-colors shadow-lg z-10"
                  title="Remover foto"
                >
                  <X className="h-4 w-4" />
                </button>

                {/* Botão de foto principal */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setPrimaryPhoto(index)
                  }}
                  className={`absolute top-2 left-2 p-1.5 rounded-full transition-colors shadow-lg z-10 ${
                    index === primaryPhotoIndex
                      ? 'bg-amber-500 text-white'
                      : 'bg-card/80 text-muted-foreground hover:bg-amber-500 hover:text-white'
                  }`}
                  title={index === primaryPhotoIndex ? 'Foto Principal' : 'Definir como Principal'}
                >
                  <Star className={`h-4 w-4 ${index === primaryPhotoIndex ? 'fill-current' : ''}`} />
                </button>

                {/* Indicador de ordem */}
                <div className="absolute bottom-2 left-2 bg-foreground/70 text-background px-2 py-0.5 rounded text-xs font-medium tabular-nums">
                  {index + 1}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Lightbox */}
        <ImageLightbox
          images={photos}
          currentIndex={lightboxIndex}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          onNavigate={setLightboxIndex}
        />

        {photos.length > 0 && (
          <div className="flex items-start gap-2 p-3 bg-accent/5 border border-border rounded-md">
            <Info className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
            <p className="text-sm text-foreground">
              Clique na <strong>estrela</strong> para definir a foto principal. A primeira foto aparecerá nas listagens.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
